import Navbar from '@/components/Navbar';
import { Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { TrustpilotWidget } from '@/components/Trustpilot';
import CookieNotice from '@/components/CookieNotice';
import FloatingCalculator from '@/components/FloatingCalculator';
import InstallPrompt from '@/components/InstallPrompt';
import { SKIMLINKS_PUBLISHER_ID } from '@/config/affiliate';
import { useAuth } from '@/lib/auth';
import { applyBrand, resetBrand } from '@/lib/brand';
import WorkspaceShell from '@/components/WorkspaceShell';
import { isWorkspacePath } from '@/lib/workspaceNav';

// Marketing / pre-login routes always keep TradesMate's default orange.
const DEFAULT_BRAND_ROUTES = ['/', '/login', '/register', '/pricing', '/forgot-password', '/reset-password'];

export default function Layout() {
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Drive the whole app accent from the signed-in user's saved brand colour.
  useEffect(() => {
    if (!DEFAULT_BRAND_ROUTES.includes(pathname) && user && user.brand_color) {
      applyBrand(user.brand_color);
    } else {
      resetBrand();
    }
  }, [user, pathname]);

  // Load the Skimlinks auto-affiliate script once, only when configured.
  useEffect(() => {
    if (!SKIMLINKS_PUBLISHER_ID || document.getElementById('skimlinks-script')) return;
    const s = document.createElement('script');
    s.id = 'skimlinks-script';
    s.type = 'text/javascript';
    s.async = true;
    s.src = `https://s.skimresources.com/js/${SKIMLINKS_PUBLISHER_ID}.skimlinks.js`;
    document.body.appendChild(s);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {user && isWorkspacePath(pathname) ? <WorkspaceShell user={user}><Outlet /></WorkspaceShell> : <Outlet />}
      </main>
      {!(user && isWorkspacePath(pathname)) && (
      <footer className="bg-slate-950 text-slate-400 mt-20" data-testid="site-footer">
        <div className="h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5 space-y-4">
              <p className="font-display text-2xl text-white">TradesMate <span className="text-[10px] font-sans font-semibold uppercase tracking-[0.3em] text-gold align-middle">UK</span></p>
              <p className="font-display italic text-slate-400">Built by a tradesman, for tradesmen.</p>
              <TrustpilotWidget theme="dark" width="240px" className="min-w-[220px]" />
            </div>
            <div className="md:col-span-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold mb-4">Explore</p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/calculators" data-testid="footer-calculators" className="hover:text-white transition-colors">Calculators</a></li>
                <li><a href="/guides" data-testid="footer-guides" className="hover:text-white transition-colors">Guides</a></li>
                <li><a href="/pricing" data-testid="footer-pricing" className="hover:text-white transition-colors">Pricing</a></li>
              </ul>
            </div>
            <div className="md:col-span-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold mb-4">Company</p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/about" data-testid="footer-about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="/feedback" data-testid="footer-feedback" className="hover:text-white transition-colors">Feedback / Ideas</a></li>
                <li><a href="mailto:tyler@tradesmateuk.co.uk" data-testid="footer-email" className="hover:text-white transition-colors">tyler@tradesmateuk.co.uk</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t border-white/10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-500">
            <div className="flex flex-wrap gap-5">
              <a href="/legal/privacy" data-testid="footer-privacy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="/legal/terms" data-testid="footer-terms" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="/legal/cookies" data-testid="footer-cookies" className="hover:text-white transition-colors">Cookie Policy</a>
            </div>
            <p>© {new Date().getFullYear()} TradesMate UK</p>
          </div>
          <p className="mt-4 text-[11px] text-slate-600 max-w-3xl leading-relaxed">
            All outputs from TradesMate are estimates only and not a binding quotation. Material prices, labour rates and site conditions vary — verify before quoting a customer.
          </p>
        </div>
      </footer>
      )}
      <CookieNotice />
      <FloatingCalculator />
      <InstallPrompt />
    </div>
  );
}
